import { Progress } from '@/components/progress'
import { Button } from '@/components/ui/common/Button'
import { LoadingCircle } from '@/components/ui/loading-circle'
import * as React from 'react'

interface BalloonStageProps {
  currentBalloon: number
  totalBalloons: number
  currentEarnings: number
  totalEarnings: number
  isLoading: boolean
  isPopped: boolean
  isCashedOut: boolean
  pumpCount: number
  moneyPerPump: number
  balloonSize: number
  maxPumps: number
  onPump: () => void
  onCashOut: () => void
}

export const BalloonStage: React.FC<BalloonStageProps> = ({
  currentBalloon,
  totalBalloons,
  currentEarnings,
  totalEarnings,
  isLoading,
  isPopped,
  isCashedOut,
  pumpCount,
  moneyPerPump,
  balloonSize,
  maxPumps,
  onPump,
  onCashOut
}) => {
  const getBalloonWord = (count: number) => {
    if (count == 0) return 'napompowanych balonów'
    if (count === 1) return 'napompowany balon'
    if (count >= 2 && count <= 4) return 'napompowane balony'
    if (count >= 5) return 'napompowanych balonów'
  }

  return (
    <div className="flex flex-col items-center gap-6">
      <h2 className="text-center text-xl font-bold">
        Balon {currentBalloon} z {totalBalloons}
      </h2>
      <div className="w-full space-y-1 text-center">
        <p className="text-sm text-gray-500">Aktualne zarobki z tego balonu:</p>
        <p className="text-2xl font-bold text-[#2bcaca]">{currentEarnings} zł</p>
      </div>
      <div className="text-center text-sm text-gray-500">
        Całkowite zarobki: <span className="font-medium">{totalEarnings} zł</span>
      </div>
      <div className="flex h-48 items-center justify-center">
        {isLoading ? (
          <LoadingCircle color="primary" size="lg" />
        ) : isPopped ? (
          <div className="flex flex-col items-center gap-2">
            <div className="text-xl font-bold text-red-500">Pękł!</div>
            <div className="text-sm text-gray-500">
              Straciłeś {pumpCount * moneyPerPump} zł
            </div>
          </div>
        ) : isCashedOut ? (
          <div className="flex flex-col items-center gap-2">
            <div className="text-3xl">💰</div>
            <div className="font-medium text-green-500">Wypłacono!</div>
            <div className="text-sm">Zarobiłeś {currentEarnings} zł</div>
          </div>
        ) : (
          <div
            className="rounded-full bg-[#2b9999] transition-all duration-300"
            style={{ width: `${balloonSize}px`, height: `${balloonSize}px` }}
          />
        )}
      </div>
      <div className="flex w-full gap-4">
        <Button
          className="flex-1 bg-[#2bcaca] hover:bg-[#2b9999]"
          disabled={isPopped || isCashedOut || isLoading}
          onClick={onPump}
        >
          Pompuj balon
        </Button>
        <Button
          className="flex-1"
          disabled={pumpCount === 0 || isPopped || isCashedOut || isLoading}
          onClick={onCashOut}
          variant="outline"
        >
          Wypłać pieniądze
        </Button>
      </div>
      <div className="w-full space-y-1">
        <div className="flex justify-between text-xs text-gray-500">
          <span>
            {pumpCount} {getBalloonWord(pumpCount)}
          </span>
          <span>{maxPumps} pompowań</span>
        </div>
        <Progress className="h-2" value={(pumpCount / maxPumps) * 100} />
      </div>
    </div>
  )
}
